import { type ChartVariantType } from '@blaze-shared/elements-domain';
import { adjustColorBrightness } from '@blaze-shared/shared-util';

import { type ChartPropsModelData, type DotChartIcons } from '../chart.models';
import {
  type DatasetMeta,
  type DotChartIconWithLabel,
  type UnsortedDataMeta,
} from '../models/helper.types';

export function prepareDatasetMeta(
  unsortedDatasetMeta: UnsortedDataMeta[],
  customIcons: ChartPropsModelData['customIcons'],
  initialIconZip: DotChartIconWithLabel[],
  elementType: ChartVariantType,
  chartPaletteColors: string[] | undefined,
  labelToIcon: (label: string) => DotChartIcons,
): {
  datasetMeta: DatasetMeta[];
  svgToPrepare: { icon: DotChartIcons; colors: string[] }[];
  labelIconZip: DotChartIconWithLabel[];
} {
  const isDotChart = elementType === 'chartDot';
  const useCustomIcons = isDotChart && customIcons?.enabled;
  const labelIconZip = [...initialIconZip];

  const sortFn = useCustomIcons
    ? (a: { label: string }, b: { label: string }): number => {
        const aIndex = labelIconZip.findIndex((l) => l.label === a.label);
        const bIndex = labelIconZip.findIndex((l) => l.label === b.label);
        return aIndex - bIndex;
      }
    : (a: { label: string }, b: { label: string }): number => a.label.localeCompare(b.label);
  const tempToPrepareSvg: { icon: DotChartIcons; colors: string[] }[] = [];

  const datasetMeta = unsortedDatasetMeta
    .toSorted(sortFn)
    .map(({ dataPoints, alias, label }, datasetIndex) => {
      const index = useCustomIcons
        ? labelIconZip.findIndex((l) => l.label === label)
        : datasetIndex;

      const backgroundColor = chartPaletteColors?.[index % chartPaletteColors.length] || '';

      const labelHaveCustomIcon = labelIconZip.some((l) => l.label === label);
      const iconNotHoveredBg = adjustColorBrightness(backgroundColor, 40);

      if (isDotChart) {
        const resolvedIcon =
          labelIconZip.find((l) => l.label === label)?.icon ?? labelToIcon(label);
        tempToPrepareSvg.push({
          icon: resolvedIcon,
          colors: [backgroundColor, iconNotHoveredBg],
        });
      }

      if (!labelHaveCustomIcon) {
        labelIconZip.push({
          label,
          icon: labelToIcon(label),
        });
      }

      return {
        dataPoints,
        alias,
        label,
        backgroundColor,
        hoveredBg: adjustColorBrightness(backgroundColor, -24),
        notHoveredBg: adjustColorBrightness(backgroundColor, 12),
        iconNotHoveredBg,
      };
    });
  return { datasetMeta, svgToPrepare: tempToPrepareSvg, labelIconZip };
}
